import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@core';
import { toast } from 'sonner';
import { CALCULATION_REASONS } from '../consts/calculation.const';
import { INSURANCE_LIMIT } from '../consts/insurance.const';
import { calculateOrder } from '../store/actions';
import {
  resetCalculation,
  resetPromoCode,
  restoreAppliedPromoCode,
  setPromoCode,
} from '../store/slice';
import { getCheckoutPriceView } from '../utils/getCheckoutPriceView';

interface UseCheckoutCalculationParams {
  showId?: number;
  eventId?: number;
  selectedSeatIds: number[];
  initialTicketsPrice: number;
  insuranceAvailable: boolean;
}

export const useCheckoutCalculation = ({
  showId,
  eventId,
  selectedSeatIds,
  initialTicketsPrice,
  insuranceAvailable,
}: UseCheckoutCalculationParams) => {
  const dispatch = useAppDispatch();
  const { promoCode, calculation } = useAppSelector(state => state.checkout);
  const [isInsuranceSelected, setIsInsuranceSelected] = useState(false);
  const priceView = getCheckoutPriceView({
    calculationResponse: calculation.response,
    calculationIncludesInsurance: calculation.insurance,
    initialTicketsPrice,
    insuranceAvailable,
    isInsuranceSelected,
  });

  const handlePromoCodeChange = (nextPromoValue: string) => {
    const appliedPromoValue = promoCode.appliedValue;
    dispatch(setPromoCode(nextPromoValue));

    if (
      nextPromoValue.trim() !== '' ||
      !appliedPromoValue ||
      !showId ||
      !eventId ||
      !selectedSeatIds.length
    ) {
      return;
    }

    dispatch(
      calculateOrder({
        reason: CALCULATION_REASONS.REMOVE_PROMO,
        eventId: showId,
        sessionId: eventId,
        seatIds: selectedSeatIds,
        insurance: insuranceAvailable,
      })
    )
      .unwrap()
      .catch(() => {
        dispatch(restoreAppliedPromoCode(appliedPromoValue));
        toast.error('Не удалось удалить промокод');
      });
  };

  const handleApplyPromoCode = () => {
    const promoValue = promoCode.value.trim();

    if (!promoValue) {
      return;
    }
    if (!selectedSeatIds.length) {
      toast.error('Нет выбранных мест для проверки промокода');
      return;
    }
    if (!showId) {
      toast.error('Не удалось определить шоу');
      return;
    }
    if (!eventId) {
      toast.error('Не удалось определить сеанс');
      return;
    }

    const previouslyAppliedPromoCode = promoCode.appliedValue;
    dispatch(
      calculateOrder({
        reason: CALCULATION_REASONS.APPLY_PROMO,
        promoCode: promoValue,
        eventId: showId,
        sessionId: eventId,
        seatIds: selectedSeatIds,
        insurance: insuranceAvailable,
      })
    )
      .unwrap()
      .catch(() => {
        if (previouslyAppliedPromoCode) {
          dispatch(restoreAppliedPromoCode(previouslyAppliedPromoCode));
          toast.error('Не удалось заменить промокод');
        }
      });
  };

  const handleInsuranceToggle = async () => {
    if (!showId || !eventId || !selectedSeatIds.length || calculation.isLoading) {
      return;
    }

    const nextInsurance = !isInsuranceSelected;
    setIsInsuranceSelected(nextInsurance);

    if (!nextInsurance) {
      return;
    }

    try {
      await dispatch(
        calculateOrder({
          reason: CALCULATION_REASONS.TOGGLE_INSURANCE,
          promoCode: promoCode.appliedValue ?? undefined,
          eventId: showId,
          sessionId: eventId,
          seatIds: selectedSeatIds,
          insurance: true,
        })
      ).unwrap();
    } catch {
      setIsInsuranceSelected(false);
      toast.error('Не удалось пересчитать стоимость страховки');
    }
  };

  useEffect(() => {
    if (
      !showId ||
      !eventId ||
      !selectedSeatIds.length ||
      initialTicketsPrice <= 0 ||
      initialTicketsPrice > INSURANCE_LIMIT
    ) {
      return;
    }

    dispatch(
      calculateOrder({
        reason: CALCULATION_REASONS.INITIAL,
        eventId: showId,
        sessionId: eventId,
        seatIds: selectedSeatIds,
        insurance: insuranceAvailable,
      })
    )
      .unwrap()
      .catch(() => toast.error('Не удалось получить расчёт стоимости заказа'));
  }, [dispatch, eventId, initialTicketsPrice, insuranceAvailable, selectedSeatIds, showId]);

  useEffect(() => {
    if (!priceView.isInsuranceAvailable) {
      setIsInsuranceSelected(false);
    }
  }, [priceView.isInsuranceAvailable]);

  useEffect(() => {
    return () => {
      dispatch(resetPromoCode());
      dispatch(resetCalculation());
    };
  }, [dispatch]);

  return {
    promoCode,
    calculation,
    priceView,
    isInsuranceSelected,
    handlePromoCodeChange,
    handleApplyPromoCode,
    handleInsuranceToggle,
  };
};
